// ./src/utils/api/FormFieldService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Form } from "./FormService";
import { FormFieldType } from "./FormFieldTypesService";

// GraphQL queries
const FORM_FIELD_LIST_QUERY = gql`
  query FormFieldList($filter: FormFieldFilter, $params: PaginationAndSorting) {
    formFieldList(filter: $filter, params: $params) {
      code
      data {
        id
        formId
        orderNo
        name
        fieldType
        label
        required
        minValue
        maxValue
        maxUniqueValues
        isReplyTo
        defaultValue
        isHidden
        customErrorMessage
        messageOnUniqueness
        gibberishCheck
        options
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        formFieldType {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_GET_QUERY = gql`
  query FormFieldGet($id: ID) {
    formFieldGet(id: $id) {
      code
      data {
        id
        formId
        orderNo
        name
        fieldType
        label
        required
        minValue
        maxValue
        maxUniqueValues
        isReplyTo
        defaultValue
        isHidden
        customErrorMessage
        messageOnUniqueness
        gibberishCheck
        options
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        form {
          id
          name
          path
        }
        formFieldType {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_GET_MANY_QUERY = gql`
  query FormFieldGetMany($ids: [ID]) {
    formFieldGetMany(ids: $ids) {
      code
      data {
        id
        formId
        orderNo
        name
        fieldType
        label
        required
        minValue
        maxValue
        maxUniqueValues
        isReplyTo
        defaultValue
        isHidden
        customErrorMessage
        messageOnUniqueness
        gibberishCheck
        options
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        form {
          id
          name
          path
        }
        formFieldType {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const FORM_FIELD_CREATE_MUTATION = gql`
  mutation FormFieldCreate($params: FormFieldInput) {
    formFieldCreate(params: $params) {
      code
      data {
        id
        formId
        orderNo
        name
        fieldType
        label
        required
        minValue
        maxValue
        maxUniqueValues
        isReplyTo
        defaultValue
        isHidden
        customErrorMessage
        messageOnUniqueness
        gibberishCheck
        options
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        formFieldType {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_UPDATE_MUTATION = gql`
  mutation FormFieldUpdate(
    $where: FormFieldWhereInput
    $params: FormFieldInput
  ) {
    formFieldUpdate(where: $where, params: $params) {
      code
      data {
        id
        formId
        orderNo
        name
        fieldType
        label
        required
        minValue
        maxValue
        maxUniqueValues
        isReplyTo
        defaultValue
        isHidden
        customErrorMessage
        messageOnUniqueness
        gibberishCheck
        options
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        form {
          id
          name
          path
        }
        formFieldType {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_REMOVE_MUTATION = gql`
  mutation FormFieldRemove($where: FormFieldWhereInput) {
    formFieldRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_REMOVE_MANY_MUTATION = gql`
  mutation FormFieldRemoveMany($where: [FormFieldWhereInput]) {
    formFieldRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface FormField {
  id?: string | undefined;
  formId?: string | undefined;
  orderNo?: number | undefined;
  name?: string | undefined;
  fieldType?: number | undefined;
  label?: string | undefined;
  required?: boolean | undefined;
  minValue?: number | undefined;
  maxValue?: number | undefined;
  maxUniqueValues?: number | undefined;
  isReplyTo?: boolean | undefined;
  defaultValue?: string | undefined;
  isHidden?: boolean | undefined;
  customErrorMessage?: string | undefined;
  messageOnUniqueness?: string | undefined;
  gibberishCheck?: string | undefined;
  options?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  form?: Form;
  formFieldType?: FormFieldType;
}

export interface FormFieldInput {
  id?: string | null | undefined;
  formId?: string | null | undefined;
  orderNo?: number | null | undefined;
  name?: string | null | undefined;
  fieldType?: number | null | undefined;
  label?: string | null | undefined;
  required?: boolean | null | undefined;
  minValue?: number | null | undefined;
  maxValue?: number | null | undefined;
  maxUniqueValues?: number | null | undefined;
  isReplyTo?: boolean | null | undefined;
  defaultValue?: string | null | undefined;
  isHidden?: boolean | null | undefined;
  customErrorMessage?: string | null | undefined;
  messageOnUniqueness?: string | null | undefined;
  gibberishCheck?: string | null | undefined;
  options?: string | null | undefined;
  status?: number | null | undefined;
}

export interface FormFieldFilter {
  id?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  name?: string[] | null | undefined;
  fieldType?: number[] | null | undefined;
  required?: boolean | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface FormFieldWhereInput {
  id?: string | undefined;
}

/**
 * FormField service that handles all form field-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class FormFieldService {
  /**
   * Get a list of form fields with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: FormFieldFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single form field by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple form fields by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new form field
   */
  static async create(params: FormFieldInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      FORM_FIELD_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing form field
   */
  static async update(
    where: FormFieldWhereInput,
    params: FormFieldInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      FORM_FIELD_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a form field
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      FORM_FIELD_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple form fields
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      FORM_FIELD_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
